/**
 * LayerProvenancePanel — INIT-2026-Q3-SCENE-HIERARCHY V4.
 *
 * Fetches and displays per-layer provenance metadata from `provenance.json`
 * produced by the converter alongside the tileset. Shows producer, date,
 * contact, methodology, license, source files, and codelist info.
 *
 * Attached at the layer level (reference metadata), not per object.
 */

import { useEffect, useState } from "react";
import { tokens } from "@/design";

export interface CrsTransformInfo {
  /** Transformation method, e.g. "Helmert 7-parameter" or "EPSG:1234". */
  method?: string;
  /** Stated accuracy in metres. */
  accuracy?: number | string;
  /** Ordered pipeline steps (collapsible). Accepts a string or string list. */
  pipeline?: string | string[];
}

export interface ProvenanceData {
  producer?: string;
  date?: string;
  contact?: string;
  methodology?: string;
  license?: string;
  sourceFiles?: string[];
  crs?: string;
  codelists?: string[];
  converterVersion?: string;
  theme?: string;
  blockCount?: number;
  featureCount?: number;
  // INIT-2026-Q3-SCENE-HIERARCHY V-CRS-B — coordinate-system provenance.
  sourceCrs?: string;
  sourceCrsName?: string;
  displayCrs?: string;
  displayCrsName?: string;
  storedCrs?: string;
  verticalDatum?: string;
  transform?: CrsTransformInfo;
}

export interface LayerProvenancePanelProps {
  /** URL to the provenance.json file for this layer */
  provenanceUrl: string | null;
  /** Layer display name */
  layerName: string;
  /** Callback when panel is closed */
  onClose: () => void;
  // V-CRS-B inline fallbacks (used when provenance.json omits CRS fields).
  /** Original survey CRS (e.g. "EPSG:6697"). */
  sourceCrs?: string | null;
  /** Region-appropriate projected CRS the viewer renders in (e.g. "EPSG:6677"). */
  displayCrs?: string | null;
  /** Vertical datum, e.g. "orthometric" (MSL). */
  verticalDatum?: string | null;
}

export function LayerProvenancePanel({
  provenanceUrl,
  layerName,
  onClose,
  sourceCrs,
  displayCrs,
  verticalDatum,
}: LayerProvenancePanelProps) {
  const [data, setData] = useState<ProvenanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!provenanceUrl) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(provenanceUrl)
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.json();
      })
      .then((json) => {
        if (cancelled) return;
        setData(json as ProvenanceData);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load provenance");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [provenanceUrl]);

  // V-CRS-B — merge fetched provenance.json CRS fields with inline fallbacks.
  // Stored CRS is always EPSG:4978 (ECEF) per the standard render-frame spec.
  const crsSource = data?.sourceCrs ?? sourceCrs ?? undefined;
  const crsSourceName = data?.sourceCrsName;
  const crsDisplay = data?.displayCrs ?? displayCrs ?? undefined;
  const crsDisplayName = data?.displayCrsName;
  const crsStored = data?.storedCrs ?? "EPSG:4978";
  const crsVertical = data?.verticalDatum ?? verticalDatum ?? undefined;
  const crsTransform = data?.transform;
  const hasCrsSection = Boolean(
    crsSource || crsDisplay || crsVertical || crsTransform,
  );

  // Render nothing only when there is neither a provenance source nor any
  // inline coordinate-system info to show.
  if (!provenanceUrl && !hasCrsSection) return null;

  return (
    <div
      data-testid="layer-provenance-panel"
      style={{
        background: tokens.color.surface[0],
        border: tokens.border.thin,
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
          background: tokens.color.surface[2],
          borderBottom: tokens.border.thin,
        }}
      >
        <span
          style={{
            fontSize: tokens.font.size.sm,
            fontWeight: tokens.font.weight.semibold ?? 600,
            color: tokens.color.ink.primary,
          }}
        >
          Source &amp; Provenance — {layerName}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close provenance panel"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: tokens.font.size.sm,
            color: tokens.color.ink.muted,
            padding: 4,
          }}
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: `${tokens.space[3]}px` }}>
        {hasCrsSection && (
          <CoordinateSystemSection
            sourceCrs={crsSource}
            sourceCrsName={crsSourceName}
            displayCrs={crsDisplay}
            displayCrsName={crsDisplayName}
            storedCrs={crsStored}
            verticalDatum={crsVertical}
            transform={crsTransform}
          />
        )}
        {loading && (
          <span style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.muted }}>
            Loading provenance…
          </span>
        )}
        {error && (
          <span
            data-testid="provenance-error"
            style={{ fontSize: tokens.font.size.xs, color: tokens.color.semantic.danger }}
          >
            {error}
          </span>
        )}
        {data && (
          <div style={{ display: "flex", flexDirection: "column", gap: tokens.space[2] }}>
            {data.producer && <ProvenanceRow label="Producer" value={data.producer} />}
            {data.date && <ProvenanceRow label="Date" value={data.date} />}
            {data.contact && <ProvenanceRow label="Contact" value={data.contact} />}
            {data.methodology && <ProvenanceRow label="Methodology" value={data.methodology} />}
            {data.license && <ProvenanceRow label="License" value={data.license} />}
            {data.crs && <ProvenanceRow label="CRS" value={data.crs} />}
            {data.theme && <ProvenanceRow label="Theme" value={data.theme} />}
            {data.converterVersion && (
              <ProvenanceRow label="Converter" value={data.converterVersion} />
            )}
            {data.featureCount != null && (
              <ProvenanceRow label="Features" value={String(data.featureCount)} />
            )}
            {data.blockCount != null && (
              <ProvenanceRow label="Blocks" value={String(data.blockCount)} />
            )}
            {data.sourceFiles && data.sourceFiles.length > 0 && (
              <div>
                <span
                  style={{
                    fontSize: tokens.font.size.xs,
                    color: tokens.color.ink.secondary,
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Source files
                </span>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 16,
                    fontSize: tokens.font.size.xs,
                    color: tokens.color.ink.primary,
                  }}
                >
                  {data.sourceFiles.map((f, i) => (
                    <li key={i} style={{ wordBreak: "break-all" }}>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.codelists && data.codelists.length > 0 && (
              <div>
                <span
                  style={{
                    fontSize: tokens.font.size.xs,
                    color: tokens.color.ink.secondary,
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Codelists
                </span>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 16,
                    fontSize: tokens.font.size.xs,
                    color: tokens.color.ink.primary,
                    fontFamily: tokens.font.family.mono,
                  }}
                >
                  {data.codelists.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * V-CRS-B — Coordinate System section. Shows the Original (source) CRS, the
 * Rendered (display) CRS the Giro3D viewer runs in, the Stored ECEF CRS, the
 * vertical datum (MSL pass-through), and the transform method + accuracy with
 * a collapsible pipeline.
 */
function CoordinateSystemSection({
  sourceCrs,
  sourceCrsName,
  displayCrs,
  displayCrsName,
  storedCrs,
  verticalDatum,
  transform,
}: {
  sourceCrs?: string;
  sourceCrsName?: string;
  displayCrs?: string;
  displayCrsName?: string;
  storedCrs?: string;
  verticalDatum?: string;
  transform?: CrsTransformInfo;
}) {
  const [pipelineOpen, setPipelineOpen] = useState(false);

  const formatCrs = (code?: string, name?: string): string | undefined => {
    if (!code) return undefined;
    return name ? `${code} — ${name}` : code;
  };

  const verticalLabel = verticalDatum
    ? (/^ortho/i.test(verticalDatum)
        ? "Orthometric (MSL) — pass-through"
        : verticalDatum)
    : undefined;

  const pipelineSteps = Array.isArray(transform?.pipeline)
    ? transform?.pipeline
    : typeof transform?.pipeline === "string" && transform.pipeline.length > 0
      ? [transform.pipeline]
      : [];

  const transformSummary = transform
    ? [
        transform.method,
        transform.accuracy != null ? `±${transform.accuracy} m` : null,
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <div
      data-testid="crs-section"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: tokens.space[2],
        marginBottom: tokens.space[3],
        paddingBottom: tokens.space[2],
        borderBottom: tokens.border.thin,
      }}
    >
      <span
        style={{
          fontSize: tokens.font.size.xs,
          fontWeight: tokens.font.weight.semibold ?? 600,
          color: tokens.color.ink.primary,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        Coordinate system
      </span>
      {formatCrs(sourceCrs, sourceCrsName) && (
        <ProvenanceRow label="Original" value={formatCrs(sourceCrs, sourceCrsName)!} />
      )}
      {formatCrs(displayCrs, displayCrsName) && (
        <ProvenanceRow label="Rendered" value={formatCrs(displayCrs, displayCrsName)!} />
      )}
      {storedCrs && <ProvenanceRow label="Stored" value={`${storedCrs} — WGS84 ECEF`} />}
      {verticalLabel && <ProvenanceRow label="Vertical" value={verticalLabel} />}
      {transformSummary && <ProvenanceRow label="Transform" value={transformSummary} />}
      {pipelineSteps.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setPipelineOpen((open) => !open)}
            aria-expanded={pipelineOpen}
            data-testid="crs-pipeline-toggle"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              fontSize: tokens.font.size.xs,
              color: tokens.color.ink.secondary,
            }}
          >
            {pipelineOpen ? "▾" : "▸"} Pipeline
          </button>
          {pipelineOpen && (
            <ol
              data-testid="crs-pipeline"
              style={{
                margin: `${tokens.space[1]}px 0 0`,
                paddingLeft: 18,
                fontSize: tokens.font.size.xs,
                color: tokens.color.ink.primary,
                fontFamily: tokens.font.family.mono,
              }}
            >
              {pipelineSteps.map((step, i) => (
                <li key={i} style={{ wordBreak: "break-all" }}>
                  {step}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

function ProvenanceRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "35% 65%",
        gap: tokens.space[2],
        alignItems: "start",
      }}
    >
      <span
        style={{
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.secondary,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.primary,
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  );
}
